/* THE SCREEN'S ERRORS FOR THE IMPROVEMENT LOOP (Jay, System Project task #1137, 02/11): a script error, a failed promise nobody
   caught, and a server the page could not reach (network down, 502–504 — the backend never saw those requests, so it cannot log
   them itself). Each goes to POST /api/loop/error, where the same error groups into one row with a count.
   Never in the way: every send is fire-and-forget through the browser's own fetch (not the session guard's wrapper, so a report
   can never trigger a sign-in redirect), the same error is sent at most once a minute, and a page sends at most 30. */
import { BACKEND_URL } from './backendConfig';

const nativeFetch = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : null;
const lastSent = new Map();
let sentThisPage = 0;
/* a page being left cuts its own calls off (the usage beat's last send, a list still loading): that is the browser, not the
   server, so nothing is reported from the moment the page starts to go */
let leaving = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { leaving = true; });
  window.addEventListener('beforeunload', () => { leaving = true; });
  window.addEventListener('pageshow', () => { leaving = false; });
}

const screenNow = () => (typeof window !== 'undefined' ? window.location.pathname : '');

const send = (payload) => {
  try {
    if (!nativeFetch || sentThisPage >= 30 || leaving) return;
    const key = `${payload.kind}|${String(payload.message).slice(0, 200)}|${payload.api || ''}`;
    const now = Date.now();
    if (now - (lastSent.get(key) || 0) < 60000) return;
    lastSent.set(key, now);
    sentThisPage += 1;
    nativeFetch(`${BACKEND_URL}/api/loop/error`, {
      method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, screen: screenNow() }),
    }).catch(() => {});
  } catch (e) { /* silent */ }
};

/* the noise every browser makes that is nobody's bug: extensions, cross-origin "Script error.", ResizeObserver's warning */
const isNoise = (message, file) => /ResizeObserver loop|^Script error\.?$/i.test(String(message || ''))
  || /^(chrome|moz|safari)-extension:/i.test(String(file || ''));

const stackOf = (err) => (err && err.stack ? String(err.stack).split('\n').slice(0, 6).join('\n') : null);

export const installLoopCapture = () => {
  if (typeof window === 'undefined' || window.__loopCapture) return;
  window.__loopCapture = true;
  window.addEventListener('error', (ev) => {
    try {
      if (!ev || (ev.target !== window && ev.target && ev.target.tagName)) return;   // a broken <img> is not a script error
      if (isNoise(ev.message, ev.filename)) return;
      send({ kind: 'screen_error', message: String(ev.message || (ev.error && ev.error.message) || 'Script error'), detail: stackOf(ev.error)
        || (ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : null) });
    } catch (e) { /* silent */ }
  });
  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const r = ev && ev.reason;
      // an axios / fetch failure the screen did not catch is already counted where it happened (server 5xx, or network below)
      if (r && (r.isAxiosError || r.name === 'AbortError')) return;
      const message = r && r.message ? r.message : String(r || 'Unhandled promise rejection');
      if (isNoise(message)) return;
      send({ kind: 'screen_promise', message, detail: stackOf(r) });
    } catch (e) { /* silent */ }
  });
};

/* the session guard tells us when one of our own API calls could not reach the server */
export const reportUnreachable = (url, status) => {
  try {
    const path = String(url || '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
    // our own reports, and the usage beat (telemetry, sent as the page closes) are never errors to report
    if (path.startsWith('/api/loop/') || path.startsWith('/api/portal/usage/')) return;
    send({ kind: 'screen_network', message: status ? `HTTP ${status} from the gateway` : 'Network error — the server could not be reached', api: path });
  } catch (e) { /* silent */ }
};

export default installLoopCapture;
